import * as React from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AjouterIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import SupprimerIcon from '@mui/icons-material/PersonRemoveOutlined';
import AdministrateurIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BibliothequeIcon from '@mui/icons-material/LocalLibraryOutlined';
import CoachIcon from '@mui/icons-material/SportsOutlined';
import QuitterIcon from '@mui/icons-material/LogoutOutlined';
import { Link } from 'react-router-dom';

import AideRoles from './AideRoles.tsx';

import {
  changerCoachAthlete,
  chargerClub,
  definirRolesMembre,
  inviterCoachClub,
  renommerClub,
  retirerMembreClub,
  useMoi,
  type Club as ClubType,
  type MembreClub,
} from '../Auth/moi.ts';

const nomMembre = (m: MembreClub) => m.nom || m.email;

function AvatarMembre({ membre }: { membre: MembreClub }) {
  const nom = nomMembre(membre);
  return (
    <Avatar alt={nom} src={membre.photo ?? undefined} imgProps={{ referrerPolicy: 'no-referrer' }} sx={{ width: 36, height: 36 }}>
      {nom.charAt(0).toUpperCase()}
    </Avatar>
  );
}

/**
 * Page Club : le club de l'utilisateur (ClubService cote serveur). Les
 * administrateurs gerent les coachs (invitation, roles), les athletes (leur
 * coach dans le club) et le nom du club ; les coachs partagent la
 * bibliotheque de seances du club, que ses bibliothecaires modifient ; un
 * athlete voit son club et peut le quitter.
 */
export default function Club() {
  const moi = useMoi();
  // undefined : chargement ; null : pas de club.
  const [club, setClub] = React.useState<ClubType | null | undefined>(undefined);
  const [nom, setNom] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [message, setMessage] = React.useState<{ severity: 'success' | 'error'; texte: string } | null>(null);

  React.useEffect(() => {
    chargerClub()
      .then(setClub)
      .catch((e) => {
        setClub(null);
        setMessage({ severity: 'error', texte: 'Lecture du club impossible : ' + (e as Error).message });
      });
  }, []);

  React.useEffect(() => {
    setNom(club?.nom ?? '');
  }, [club]);

  const agir = async (action: () => Promise<ClubType | null>, succes?: string) => {
    setEnCours(true);
    setMessage(null);
    try {
      setClub(await action());
      if (succes) setMessage({ severity: 'success', texte: succes });
      return true;
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Action impossible : ' + (e as Error).message });
      return false;
    } finally {
      setEnCours(false);
    }
  };

  const inviter = async () => {
    const adresse = email.trim();
    if (await agir(() => inviterCoachClub(adresse), `Invitation envoyée à ${adresse} : elle lui sera proposée à sa prochaine connexion.`)) {
      setEmail('');
    }
  };

  const retirer = (m: MembreClub) => {
    let question: string;
    if (m.moi) {
      question = club?.coach
        ? 'Quitter le club ? Tu ne verras plus sa bibliothèque ; tes séances personnelles et les calendriers sont conservés. Tu retrouveras ta propre bibliothèque.'
        : 'Quitter le club ? Tu quittes aussi ton coach, qui n\'aura plus accès à ton calendrier (il est conservé).';
    } else if (m.enAttente) {
      question = `Annuler l'invitation envoyée à ${m.email} ?`;
    } else if (m.coach) {
      question = `Retirer ${nomMembre(m)} du club ? Il ne verra plus la bibliothèque ; ses séances et les calendriers sont conservés.`;
    } else {
      question = `Retirer ${nomMembre(m)} du club ? Son coach n'aura plus accès à son calendrier (il est conservé).`;
    }
    if (!window.confirm(question)) return;
    agir(() => retirerMembreClub(m.id), m.moi ? 'Tu as quitté le club.' : undefined);
  };

  const zoneMessage = message && (
    <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.texte}</Alert>
  );

  const page = (contenu: React.ReactNode) => (
    <Box component="main" sx={{ p: { xs: 2, md: 12 }, pt: { xs: 9, md: 10 }, maxWidth: 900 }}>
      {zoneMessage}
      {contenu}
    </Box>
  );

  if (club === undefined) return page(null);

  if (club === null) {
    return page(
      <>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>Club</Typography>
        <Typography color="text.secondary">
          Tu ne fais partie d'aucun club. Tu y entreras en acceptant l'invitation d'un coach.
        </Typography>
      </>,
    );
  }

  const admin = club.administrateur;
  const coachs = club.membres.filter((m) => m.coach);
  const coachsActifs = coachs.filter((m) => !m.enAttente);
  const athletes = club.membres.filter((m) => m.athlete);
  // Encadrement : coachs et administrateurs qui n'entrainent pas.
  const encadrement = club.membres.filter((m) => !m.athlete);
  const administrateurSeul = club.administrateur && !club.coach && !club.athlete;
  // Adhesion de l'utilisateur (absente de la liste s'il n'est que lecteur).
  const moiMembre = club.membres.find((m) => m.moi);
  // Le club garde toujours un administrateur : le dernier ne se revoque pas
  // et ne part pas tant qu'il reste d'autres membres (cf ClubService).
  const membresActifs = club.membres.filter((m) => !m.enAttente);
  const nbAdministrateurs = membresActifs.filter((m) => m.administrateur).length;
  const dernierAdmin = (m: MembreClub) => m.administrateur && !m.enAttente && nbAdministrateurs <= 1;
  const departBloque = (m: MembreClub) => dernierAdmin(m) && membresActifs.length > 1;

  const actionsMembre = (m: MembreClub) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {admin && !m.enAttente && (
        <Tooltip
          title={dernierAdmin(m)
            ? 'Dernier administrateur du club : nomme d\'abord un autre administrateur'
            : m.administrateur ? 'Retirer le rôle d\'administrateur' : 'Nommer administrateur'}
        >
          <span>
            <IconButton
              aria-label="Administrateur"
              color={m.administrateur ? 'primary' : 'default'}
              disabled={enCours || dernierAdmin(m)}
              onClick={() => agir(() => definirRolesMembre(m.id, { administrateur: !m.administrateur }))}
            >
              <AdministrateurIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {admin && !m.athlete && !m.enAttente && (m.coach || m.profilCoach) && (
        <Tooltip
          title={m.coach
            ? (m.nbAthletes > 0
              ? 'Confie d\'abord ses athlètes à un autre coach pour qu\'il n\'entraîne plus'
              : 'Ne plus entraîner : administrateur seulement, sans calendrier ni séances, hors facturation')
            : 'Entraîner dans le club (coach)'}
        >
          <span>
            <IconButton
              aria-label="Coach"
              color={m.coach ? 'primary' : 'default'}
              disabled={enCours || (m.coach && (m.nbAthletes > 0 || !m.administrateur))}
              onClick={() => agir(() => definirRolesMembre(m.id, { coach: !m.coach }))}
            >
              <CoachIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {admin && m.coach && !m.enAttente && (
        <Tooltip title={m.bibliothecaire ? 'Ne plus gérer la bibliothèque' : 'Confier la gestion de la bibliothèque'}>
          <IconButton
            aria-label="Bibliothécaire"
            color={m.bibliothecaire ? 'primary' : 'default'}
            disabled={enCours}
            onClick={() => agir(() => definirRolesMembre(m.id, { bibliothecaire: !m.bibliothecaire }))}
          >
            <BibliothequeIcon />
          </IconButton>
        </Tooltip>
      )}
      {(admin || m.moi) && !(m.moi && club.personnel) && (
        <Tooltip
          title={departBloque(m)
            ? 'Dernier administrateur du club : nomme d\'abord un autre administrateur'
            : m.moi ? 'Quitter le club' : m.enAttente ? 'Annuler l\'invitation' : 'Retirer du club'}
        >
          <span>
            <IconButton
              edge="end"
              aria-label={m.moi ? 'Quitter' : 'Retirer'}
              disabled={enCours || departBloque(m)}
              onClick={() => retirer(m)}
            >
              {m.moi ? <QuitterIcon /> : <SupprimerIcon />}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );

  const chipsMembre = (m: MembreClub) => (
    <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
      {m.nom && <span>{m.email}</span>}
      {m.enAttente && <Chip size="small" color="warning" variant="outlined" label="Invitation en attente" />}
      {!m.enAttente && !m.nom && <Chip size="small" color="warning" variant="outlined" label="Première connexion attendue" />}
      {m.administrateur && <Chip size="small" color="primary" variant="outlined" label="Administrateur" />}
      {m.bibliothecaire && <Chip size="small" color="info" variant="outlined" label="Bibliothécaire" />}
      {!m.coach && !m.athlete && !m.enAttente && <Chip size="small" variant="outlined" label="N'entraîne pas" />}
      {m.coach && !m.enAttente && (admin || m.moi) && (
        <span>{m.nbAthletes} athlète{m.nbAthletes > 1 ? 's' : ''}</span>
      )}
    </Box>
  );

  return page(
    <>
      {admin ? (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (nom.trim() && nom.trim() !== club.nom) agir(() => renommerClub(nom.trim()), 'Club renommé.');
          }}
          sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', maxWidth: 560, mb: 1 }}
        >
          <TextField size="small" label="Nom du club" value={nom} onChange={(e) => setNom(e.target.value)} sx={{ flex: 1 }} />
          <Button type="submit" variant="outlined" disabled={enCours || !nom.trim() || nom.trim() === club.nom}>Renommer</Button>
        </Box>
      ) : (
        <Typography variant="h5" component="h1" sx={{ mb: 1 }}>{club.nom}</Typography>
      )}

      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {club.coach
          ? <>
              {club.nbCoachs} coach{club.nbCoachs > 1 ? 's' : ''} partage{club.nbCoachs > 1 ? 'nt' : ''} une bibliothèque
              de {club.nbSeancesBibliotheque} séance{club.nbSeancesBibliotheque > 1 ? 's' : ''} (page <Link to="/seances">Séances</Link>).{' '}
              {club.bibliothecaire
                ? 'Tu y publies tes séances et tu peux les modifier.'
                : 'Seuls les bibliothécaires la modifient : duplique une séance du club pour l\'adapter à ta façon.'}
            </>
          : administrateurSeul
            ? 'Tu administres le club sans y entraîner : tu n\'as ni calendrier, ni journal, ni séances, et tu ne comptes pas parmi ses coachs.'
            : <>Ton coach{moi?.coach ? <> : <b>{moi.coach}</b></> : ' : aucun pour l\'instant, un administrateur du club peut t\'en attribuer un.'}</>}
      </Typography>

      {admin && moiMembre && dernierAdmin(moiMembre) && !club.personnel && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Tu es le seul administrateur du club : il doit toujours en avoir un. Pour retirer ton rôle ou quitter le club,
          nomme d'abord un autre administrateur.
        </Alert>
      )}

      <AideRoles
        mesRoles={[
          ...(club.administrateur ? ['administrateur'] : []),
          ...(club.coach ? ['coach'] : []),
          ...(club.athlete ? ['athlete'] : []),
          ...(club.bibliothecaire ? ['bibliothecaire'] : []),
        ]}
      />

      {club.personnel && admin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Tu es pour l'instant le seul coach de ton club. Donne-lui le nom de ta structure et invite d'autres coachs pour
          partager ta bibliothèque de séances ; tes athlètes sont automatiquement membres du club.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>Coachs et administrateurs</Typography>
        {admin && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim()) inviter();
            }}
            sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', maxWidth: 560, mb: 1 }}
          >
            <TextField
              size="small"
              type="email"
              label="Adresse Google du coach à inviter"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Button type="submit" variant="contained" startIcon={<AjouterIcon />} disabled={enCours || !email.trim()}>
              Inviter
            </Button>
          </Box>
        )}
        <List>
          {encadrement.map((m) => (
            <ListItem key={m.id} divider secondaryAction={actionsMembre(m)} sx={{ pr: 22 }}>
              <ListItemAvatar><AvatarMembre membre={m} /></ListItemAvatar>
              <ListItemText
                primary={nomMembre(m) + (m.moi ? ' (toi)' : '')}
                secondary={chipsMembre(m)}
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {admin && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 1 }}>Athlètes ({athletes.length})</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Un athlète entre dans le club en acceptant l'invitation d'un de ses coachs (page <Link to="/athletes">Athlètes</Link>).
            Tu peux le confier à un autre coach du club : celui-ci accède alors à son calendrier, sans nouvelle invitation.
            Tu ne vois pas son entraînement.
          </Typography>
          {athletes.length === 0 && (
            <Typography variant="body2" color="text.secondary">Aucun athlète dans le club pour l'instant.</Typography>
          )}
          <List>
            {athletes.map((m) => (
              <ListItem key={m.id} divider secondaryAction={actionsMembre(m)} sx={{ pr: 12, flexWrap: 'wrap', gap: 1 }}>
                <ListItemAvatar><AvatarMembre membre={m} /></ListItemAvatar>
                <ListItemText
                  primary={nomMembre(m) + (m.moi ? ' (toi)' : '')}
                  secondary={chipsMembre(m)}
                  secondaryTypographyProps={{ component: 'div' }}
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  select
                  size="small"
                  label="Coach"
                  value={m.idCoachSuivi ?? ''}
                  disabled={enCours || coachsActifs.length === 0}
                  onChange={(e) => {
                    const coach = coachsActifs.find((c) => c.id === e.target.value);
                    if (coach && coach.id !== m.idCoachSuivi) {
                      agir(() => changerCoachAthlete(m.id, coach.id), `${nomMembre(m)} est maintenant suivi par ${nomMembre(coach)}.`);
                    }
                  }}
                  sx={{ minWidth: 200 }}
                >
                  {!m.idCoachSuivi && <MenuItem value="" disabled>{m.coachSuivi ?? 'Aucun coach'}</MenuItem>}
                  {coachsActifs.map((c) => <MenuItem key={c.id} value={c.id}>{nomMembre(c)}</MenuItem>)}
                </TextField>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {!club.coach && !admin && moiMembre && (
        <Button color="error" variant="outlined" startIcon={<QuitterIcon />} disabled={enCours} onClick={() => retirer(moiMembre)}>
          Quitter le club
        </Button>
      )}
    </>,
  );
}
